import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  Mutation,
  Query,
  ProfilePreferencesInput,
} from '../types/generated/graphql';

/**
 * Fetch a single profile's owner-authored preferences (age, gender,
 * content policies). Used by the per-profile settings dialog —
 * separated from the YouTubeProfile query because preferences live
 * in their own collection and are mutated independently.
 */
export const useGetProfilePreferences = (
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string | null,
) => {
  type T = Pick<Query, 'getProfilePreferences'>;
  return useQuery<T>(QUERY_GET_PROFILE_PREFERENCES, {
    client,
    variables: {profileId: profileId ?? ''},
    skip: !profileId,
    fetchPolicy: 'network-only',
  });
};

const QUERY_GET_PROFILE_PREFERENCES = gql`
  query getProfilePreferences($profileId: ID!) {
    getProfilePreferences(profileId: $profileId) {
      profileId
      birthdate
      age
      gender
      religionPolicyMode
      religionOwnFaith
      politicsPolicyMode
      languagePolicyMode
      languageList
      updatedAt
    }
  }
`;

/**
 * Owner-only mutation. The backend rejects callers that aren't the
 * linked owner of the profile (or admin). Returns the merged result
 * so the cache picks up the new values without a separate refetch.
 */
export const useSetProfilePreferences = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'setProfilePreferences'>;
  return useMutation<
    T,
    {profileId: string; input: ProfilePreferencesInput}
  >(MUTATION_SET_PROFILE_PREFERENCES, {
    client,
    refetchQueries: ['getProfilePreferences', 'fct'],
    // Block the mutation promise until the getProfilePreferences
    // refetch lands. ProfileLayoutResponsive is the PERSISTENT layout
    // for both /profile/:pid/setup and /profile/:pid — its gate reads
    // getProfilePreferences. Without this, ProfileSetup navigates to
    // the dashboard while that refetch is still in flight; the gate
    // re-evaluates against the stale (pre-save, no-birthdate) cache
    // and bounces straight back to /setup. Awaiting the refetch means
    // the gate sees the saved birthdate on the first save.
    awaitRefetchQueries: true,
  });
};

const MUTATION_SET_PROFILE_PREFERENCES = gql`
  mutation setProfilePreferences(
    $profileId: ID!
    $input: ProfilePreferencesInput!
  ) {
    setProfilePreferences(profileId: $profileId, input: $input) {
      profileId
      birthdate
      age
      gender
      religionPolicyMode
      politicsPolicyMode
      languagePolicyMode
      languageList
      updatedAt
    }
  }
`;
